import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import orderAPI from "../../../api/orderApi";
import { usePdfDownload } from "./usePdfDownload";
import StoreAPI from "../../../api/StoreApi";
import Auth from "../../../utils/AuthUtils";
import LoadingScreen from "../../../components/partials/global/LoadingScreen";

export default function Invoice() {
  /************ state declaration******** */
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [storeDetails, setStoreDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const invoiceRef = useRef(null);
  const { downloadPdf, isGenerating } = usePdfDownload();

  /********** get details of order and store ***************/
  useEffect(() => {
    if (sessionStorage.getItem("storeDetails")) {
      setStoreDetails(JSON.parse(sessionStorage.getItem("storeDetails")));
    } else {
      fetchGeneralDetails();
    }

    fetchOrderDetails(id);
  }, [id]);

  const fetchOrderDetails = async (orderId) => {
    try {
      const res = await orderAPI.details({ orderId });
      if (res.code === 200) {
        setOrder(res.data);
      }
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const fetchGeneralDetails = async () => {
    try {
      const res = await StoreAPI.getGeneralDetails(
        Auth.getUserDetails("storeId"),
      );

      if (res.code === 200) {
        const result = res.data;
        setStoreDetails(result);
        sessionStorage.setItem("storeDetails", JSON.stringify(result));
      }
    } catch (err) {}
  };
  /********** end get details of order and store ***************/

  const handleDownload = () => {
    downloadPdf(invoiceRef, `invoice-${order?.orderId || id}.pdf`, {
      labelMode: false,
    });
  };

  if (loading) return <LoadingScreen text="Generating Invoice..." />;
  if (!order) return <LoadingScreen text="Order not Found" />;

  const address = order?.address || {};
  const user = order?.user || {};
  const payment = order?.payment || {};
  const items = order?.items || [];

  const invoiceDate = order.orderDate
    ? new Date(order.orderDate).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : new Date().toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });

  const customerName = `${address.firstName || user.firstName || ""} ${
    address.lastName || user.lastName || ""
  }`.trim();

  const subtotal = items.reduce(
    (sum, i) => sum + (i.price || 0) * (i.quantity || 1),
    0,
  );
  const taxAmount = order.tax || Math.round(subtotal * 0.18);
  const shippingAmount = order.shipping || 0;
  const total = order.total || subtotal + taxAmount + shippingAmount;

  return (
    <>
      <div
        className="content"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div className="mb-3">
          <button
            onClick={handleDownload}
            className="btn btn-success me-2"
            disabled={isGenerating}
            style={{
              cursor: isGenerating ? "not-allowed" : "pointer",
              opacity: isGenerating ? 0.7 : 1,
            }}
          >
            {isGenerating ? "Generating PDF..." : "Download Invoice"}
          </button>

          <Link to={`/orders/details/${id}`} className="btn btn-danger">
            Close
          </Link>
        </div>

        <div
          ref={invoiceRef}
          style={{
            width: "794px",
            background: "#fff",
            fontFamily: "Arial, sans-serif",
            boxShadow: "0 4px 24px rgba(0,0,0,0.2)",
          }}
        >
          <div style={{ height: "6px", background: "#1a1a2e" }} />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              padding: "28px 36px 20px",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  color: "#1a1a2e",
                  marginBottom: "6px",
                }}
              >
                {storeDetails?.name}
              </div>

              {storeDetails?.gstin && (
                <div
                  style={{
                    fontSize: "10px",
                    color: "#888",
                    marginBottom: "2px",
                  }}
                >
                  GSTIN:{" "}
                  <strong style={{ color: "#333" }}>
                    {storeDetails?.gstin}
                  </strong>
                </div>
              )}
              <div
                style={{
                  fontSize: "11px",
                  color: "#555",
                  maxWidth: "280px",
                  lineHeight: 1.5,
                }}
              >
                {storeDetails?.address}, {storeDetails?.district},{" "}
                {storeDetails?.state} - {storeDetails?.pincode}
              </div>
              <div
                style={{ fontSize: "11px", color: "#555", marginTop: "2px" }}
              >
                Ph: {storeDetails?.phone}
              </div>
              <div style={{ fontSize: "11px", color: "#555" }}>
                {storeDetails?.email}
              </div>
            </div>

            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontSize: "30px",
                  fontWeight: "bold",
                  color: "#1a1a2e",
                  letterSpacing: "2px",
                }}
              >
                INVOICE
              </div>
              <table style={{ marginLeft: "auto", marginTop: "10px" }}>
                <tbody>
                  {[
                    ["Invoice No.", `INV-${order.orderId}`],
                    ["Order ID", `#${order.orderId}`],
                    ["Date", invoiceDate],
                  ].map(([label, value]) => (
                    <tr key={label}>
                      <td
                        style={{
                          fontSize: "11px",
                          color: "#888",
                          padding: "2px 10px 2px 0",
                          textAlign: "right",
                        }}
                      >
                        {label}
                      </td>
                      <td
                        style={{
                          fontSize: "12px",
                          fontWeight: "bold",
                          color: "#1a1a2e",
                          padding: "2px 0",
                        }}
                      >
                        {value}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td
                      style={{
                        fontSize: "11px",
                        color: "#888",
                        padding: "2px 10px 2px 0",
                        textAlign: "right",
                      }}
                    >
                      Payment
                    </td>
                    <td style={{ padding: "2px 0" }}>
                      <span
                        style={{
                          display: "inline-block",
                          background:
                            payment.status === "paid" ? "#e6f4ea" : "#fff3cd",
                          color:
                            payment.status === "paid" ? "#1a7f4b" : "#856404",
                          fontSize: "10px",
                          fontWeight: "bold",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          textTransform: "uppercase",
                        }}
                      >
                        {payment.status || "Pending"}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              padding: "20px 36px",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <div
              style={{ paddingRight: "24px", borderRight: "1px solid #e0e0e0" }}
            >
              <SectionLabel>Bill To</SectionLabel>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  color: "#1a1a2e",
                  marginBottom: "4px",
                }}
              >
                {customerName}
              </div>
              <div style={{ fontSize: "11px", color: "#555", lineHeight: 1.6 }}>
                {user.email}, {user.mobile}
                {order?.gstin && (
                  <div>
                    <b>GST: </b>
                    {order?.gstin}
                  </div>
                )}
              </div>

              <div style={{ fontSize: "11px", color: "#555", lineHeight: 1.6 }}>
                {address.addressLine1 && <div>{address.addressLine1}</div>}
                {address.addressLine2 && <div>{address.addressLine2}</div>}
                <div>
                  {[address.district, address.state].filter(Boolean).join(", ")}
                </div>
                {address.pinCode && <div>PIN: {address.pinCode}</div>}
                {address.country && <div>{address.country}</div>}
              </div>
            </div>
            <div style={{ paddingLeft: "24px" }}>
              <SectionLabel>Ship To</SectionLabel>
              {order.isShippingSameasBilling === false ? (
                <>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "#1a1a2e",
                      marginBottom: "4px",
                    }}
                  >
                    {order?.shippingAddress.firstName}{" "}
                    {order?.shippingAddress.lastName}
                  </div>
                  <div
                    style={{ fontSize: "11px", color: "#555", lineHeight: 1.6 }}
                  >
                    {order?.shippingAddress?.mobileNumber}

                    {order?.shippingAddress.addressLine1 && (
                      <div>{order?.shippingAddress.addressLine1}</div>
                    )}
                    {order?.shippingAddress.addressLine2 && (
                      <div>{order?.shippingAddress.addressLine2}</div>
                    )}
                    <div>
                      {[
                        order?.shippingAddress.district,
                        order?.shippingAddress.state,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </div>
                    {order?.shippingAddress.pinCode && (
                      <div>PIN: {order?.shippingAddress.pinCode}</div>
                    )}
                    {order?.shippingAddress.country && (
                      <div>{order?.shippingAddress.country}</div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "#1a1a2e",
                      marginBottom: "4px",
                    }}
                  >
                    {customerName}
                  </div>
                  <div
                    style={{ fontSize: "11px", color: "#555", lineHeight: 1.6 }}
                  >
                    {address.addressLine1 && <div>{address.addressLine1}</div>}
                    {address.addressLine2 && <div>{address.addressLine2}</div>}
                    <div>
                      {[address.district, address.state]
                        .filter(Boolean)
                        .join(", ")}
                    </div>
                    {address.pinCode && <div>PIN: {address.pinCode}</div>}
                    {address.country && <div>{address.country}</div>}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Items Table */}
          <div style={{ padding: "24px 36px 0" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {[
                    "#",
                    "Item Description",
                    "Qty",
                    "MRP",
                    "Price",
                    "Amount",
                  ].map((h, i) => (
                    <th
                      key={h}
                      style={{
                        padding: "8px 10px",
                        fontSize: "11px",
                        fontWeight: "bold",
                        textTransform: "uppercase",
                        color: "#fff",
                        background: "#1a1a2e",
                        textAlign:
                          i === 0 || i === 2
                            ? "center"
                            : i > 2
                              ? "right"
                              : "left",
                        width:
                          i === 0
                            ? "36px"
                            : i === 2
                              ? "60px"
                              : i > 2
                                ? "95px"
                                : "auto",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr
                    key={idx}
                    style={{ background: idx % 2 === 0 ? "#fff" : "#fafafa" }}
                  >
                    <td style={tdStyle("center")}>{idx + 1}</td>
                    <td style={tdStyle("left")}>
                      <div style={{ fontWeight: "bold", color: "#1a1a2e" }}>
                        {item.productName}
                      </div>
                      {item.variantName && (
                        <div
                          style={{
                            fontSize: "10px",
                            color: "#888",
                            marginTop: "2px",
                          }}
                        >
                          Variant: {item.variantName}
                        </div>
                      )}
                    </td>
                    <td style={tdStyle("center")}>{item.quantity}</td>
                    <td
                      style={{
                        ...tdStyle("right"),
                        color: "#aaa",
                        textDecoration: "line-through",
                      }}
                    >
                      ₹{Number(item.mrp || item.price).toLocaleString("en-IN")}
                    </td>
                    <td style={tdStyle("right")}>
                      ₹{Number(item.price).toLocaleString("en-IN")}
                    </td>
                    <td
                      style={{
                        ...tdStyle("right"),
                        fontWeight: "bold",
                        color: "#1a1a2e",
                      }}
                    >
                      ₹
                      {Number(item.price * item.quantity).toLocaleString(
                        "en-IN",
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "16px 36px 24px",
            }}
          >
            <div style={{ width: "280px" }}>
              {[
                ["Subtotal", order.subtotal],
                ...(order.discount > 0
                  ? [
                      [
                        "Discount",
                        `-₹${Number(order.discount).toLocaleString("en-IN")}`,
                      ],
                    ]
                  : []),
                ...(order.shipping > 0 ? [["Shipping", order.shipping]] : []),
                ...(order.couponCode
                  ? [["Coupon Code", order.couponCode]]
                  : []),
                // Partial Payment details if applicable
                ...(order?.payment?.method === "PARTIAL"
                  ? [
                      [
                        "Advance Paid",
                        `-₹${Number(
                          order?.payment?.partialPayment?.advanceAmount || 0,
                        ).toLocaleString("en-IN")}`,
                      ],
                    ]
                  : []),
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "6px 0",
                    borderBottom: "0.5px solid #e0e0e0",
                    fontSize: "13px",
                    fontWeight: label === "Advance Paid" ? "600" : "400",
                    color: label === "Advance Paid" ? "#2e7d32" : "#555",
                  }}
                >
                  <span>{label}</span>

                  <span>
                    {label === "Coupon Code"
                      ? value
                      : typeof value === "string"
                        ? value
                        : `₹${Number(value).toLocaleString("en-IN")}`}
                  </span>
                </div>
              ))}

              {/* Dynamic Total Box */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "10px 14px",
                  marginTop: "6px",
                  background: "#1a1a2e",
                  color: "#fff",
                  borderRadius: "6px",
                  fontSize: "15px",
                  fontWeight: "bold",
                }}
              >
                <span>
                  {order?.payment?.method === "PARTIAL" ? "Balance Due" : "Total"}
                </span>
                <span>
                  ₹
                  {Number(
                    order?.payment?.method === "PARTIAL"
                      ? (order?.payment?.partialPayment?.balanceAmount ?? order.total)
                      : order.total,
                  ).toLocaleString("en-IN")}
                </span>
              </div>
            </div>
          </div>

          {payment.method && (
            <div
              style={{
                margin: "0 36px 24px",
                padding: "12px 16px",
                background: "#f8f8f8",
                borderRadius: "6px",
                border: "0.5px solid #e0e0e0",
              }}
            >
              <SectionLabel>Payment Details</SectionLabel>
              <div
                style={{
                  display: "flex",
                  gap: "32px",
                  flexWrap: "wrap",
                  marginTop: "6px",
                }}
              >
                {[
                  { label: "Method", value: payment.method },
                  { label: "Status", value: payment.status?.toUpperCase() },
                  payment.details?.id && {
                    label: "Payment ID",
                    value: payment.details.id,
                  },
                  payment.details?.gatewayOrderId && {
                    label: "Gateway Order ID",
                    value: payment.details.gatewayOrderId,
                  },
                ]
                  .filter(Boolean)
                  .map((item) => (
                    <div key={item.label}>
                      <div style={{ fontSize: "10px", color: "#888" }}>
                        {item.label}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: "bold",
                          color: "#1a1a2e",
                        }}
                      >
                        {item.value}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div
            style={{
              padding: "16px 36px",
              borderTop: "1px solid #e0e0e0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ fontSize: "11px", color: "#888" }}>
              This is a computer-generated invoice and does not require a
              signature.
            </div>
            <div
              style={{ fontSize: "11px", fontWeight: "bold", color: "#1a1a2e" }}
            >
              {storeDetails?.name}
            </div>
          </div>
          <div style={{ height: "4px", background: "#1a1a2e" }} />
        </div>

        <div style={{ height: "24px" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </>
  );
}

// ── Small helpers ─────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <div
      style={{
        fontSize: "10px",
        fontWeight: "bold",
        textTransform: "uppercase",
        letterSpacing: "1.5px",
        color: "#888",
        marginBottom: "6px",
      }}
    >
      {children}
    </div>
  );
}

const tdStyle = (align) => ({
  padding: "9px 10px",
  fontSize: "12px",
  color: "#333",
  borderBottom: "0.5px solid #e8e8e8",
  textAlign: align,
  verticalAlign: "top",
});
